import { format, isAfter, isBefore } from 'date-fns';
import { ReactNode, Ref, useEffect, useState } from 'react';
import { DayPicker, DayPickerProps } from 'react-day-picker';
import 'react-day-picker/dist/style.css';
import { useTranslation } from 'react-i18next';
import { ButtonProps, ClearIcon, Icon, Popover, PopoverProps } from '@anytag/shared-atoms';
import { DATE_PICKER_INPUT_MASK_FORMAT, DEFAULT_DATE_FNS_FORMAT } from '@anytag/shared-constants';
import { useDeepCompareEffect } from '@anytag/shared-hooks';
import { AppLanguage, SelectedRangeOption } from '@anytag/shared-types';
import { cn, localeUtils } from '@anytag/shared-utils';
import { ActionButtons } from '../action-buttons';
import { CustomDayPickerNav, StyledInputWithMask, WithGlobalStyles } from './components';
import { getDatePickerLocale, getInputState, getInputText, placeholderMulti } from './helpers';
import { Period } from './types';
import { useCustomRange } from './use-custom-range';
import { useHandlers } from './use-date-picker-state';

export interface DayPickerRangeProps extends Pick<PopoverProps, 'align' | 'side' | 'withinPortal'> {
  period: Period;
  handleChangePeriod: (period: Period) => void;
  rangeList?: SelectedRangeOption[];
  initialPeriod?: Period;
  hasError?: boolean;
  disabled?: boolean;
  disabledRange?: DayPickerProps['disabled'];
  className?: string;
  clearTitle?: string | null;
  hasCalendarIcon?: boolean;
  saveOnBlur?: boolean;
  onClearCallback?: (isAllTimeClicked: boolean) => void;
  customInputTextNode?: ReactNode;
  type?: ButtonProps['type'];
  clearable?: boolean;
  ref?: Ref<HTMLDivElement>;
}
export const DayPickerRange = ({
  ref,
  className,
  period,
  handleChangePeriod,
  rangeList = [],
  initialPeriod,
  hasError,
  disabled,
  disabledRange,
  clearTitle = '',
  hasCalendarIcon = true,
  onClearCallback,
  customInputTextNode,
  type,
  clearable = true,
  saveOnBlur = true,
  withinPortal = true,
  align = 'start',
  side = 'bottom'
}: DayPickerRangeProps) => {
  const { i18n, t } = useTranslation();
  const { state, setState, handleDayClick, handleDayMouseEnter, handleResetClick } = useHandlers();
  const [datePickerInput, setDatePickerInput] = useState<string>(placeholderMulti);

  let handleClosePopover: () => void;

  // if form has default period values, than set picker input
  useEffect(() => {
    if (resultInput) {
      setDatePickerInput(resultInput);
    }
    if (initialPeriod) {
      setState(getInputState(initialPeriod));
    }
  }, []);

  useDeepCompareEffect(() => {
    const newInputState = getInputState(period);
    const newResultInput = getInputText(i18n.language as AppLanguage, newInputState.hoverTo, newInputState.hoverFrom);

    // Sync state value when period changed
    if (datePickerInput !== newResultInput) {
      setState((prevState) => ({ ...prevState, ...newInputState }));
      setDatePickerInput(newResultInput);
    }
  }, [period, datePickerInput]);

  const CustomRange = useCustomRange({
    state,
    onClickCallback: setState,
    initialPeriod,
    rangeList,
    clearTitle
  });

  const handleUpdate = () => {
    setDatePickerInput(resultInput || '');
    handleClosePopover();

    if (state.from && state.to) {
      if (onClearCallback) {
        onClearCallback(false);
      }
      const startDate = isBefore(state.from, state.to) ? state.from : state.to;
      const endDate = isAfter(state.to, state.from) ? state.to : state.from;
      handleChangePeriod({
        startDate: format(startDate, DEFAULT_DATE_FNS_FORMAT),
        endDate: format(endDate, DEFAULT_DATE_FNS_FORMAT)
      });
      // means just one date is selected
    } else if (state.from || state.to) {
      if (onClearCallback) {
        onClearCallback(false);
      }
      const startDate = (state.from || state.to) as Date;
      handleChangePeriod({
        startDate: format(startDate, DEFAULT_DATE_FNS_FORMAT),
        endDate: format(startDate, DEFAULT_DATE_FNS_FORMAT)
      });
    } else if (state.from === null && state.to === null) {
      if (onClearCallback) {
        onClearCallback(true);
      }
      handleChangePeriod({ startDate: '', endDate: '' });
    }
  };

  const { from, hoverFrom, to, hoverTo } = state;
  const resultInput = getInputText(i18n.language as AppLanguage, hoverTo, hoverFrom) || clearTitle || '';

  const isPlaceholder = datePickerInput === placeholderMulti;

  const handleClickAway = () => {
    if (saveOnBlur) {
      handleUpdate();
    }
  };

  const handleEnter = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleUpdate();
    }
  };

  return (
    <div className={cn('flex flex-col', className)} ref={ref} onKeyDown={handleEnter}>
      <Popover
        withinPortal={withinPortal}
        align={align}
        side={side}
        onClose={handleClickAway}
        target={({ isOpen, closePopover, setOpen }) => {
          handleClosePopover = closePopover;

          return (
            <div
              onClick={setOpen}
              className={cn(
                'bg-input-background-default border-input-border-default box-border flex h-8 w-full min-w-60 items-center gap-1 rounded-sm border p-0.5 pl-2 text-base leading-5',
                isOpen ? 'border-active' : 'border-base-border',
                { 'border-error': hasError }
              )}
            >
              {hasCalendarIcon ? <Icon icon="calendar" size="16px" variant="gray" className="calendar-icon" /> : null}
              {customInputTextNode && !isOpen ? (
                customInputTextNode
              ) : (
                <>
                  <StyledInputWithMask
                    value={state.from ? format(state.from, DATE_PICKER_INPUT_MASK_FORMAT) : ''}
                    onAcceptCallback={(parsedDate) => {
                      setState((prevState) => ({
                        ...prevState,
                        from: parsedDate,
                        hoverFrom: parsedDate
                      }));
                    }}
                  />
                  &ndash;
                  <StyledInputWithMask
                    value={state.to ? format(state.to, DATE_PICKER_INPUT_MASK_FORMAT) : ''}
                    className="ml-0.5"
                    onAcceptCallback={(parsedDate) => {
                      setState((prevState) => ({
                        ...prevState,
                        to: parsedDate,
                        hoverTo: parsedDate
                      }));
                    }}
                  />
                </>
              )}
              {!isPlaceholder && datePickerInput && clearable && !disabled ? (
                <ClearIcon
                  onClick={(e) => {
                    e.stopPropagation();
                    setDatePickerInput('');
                    handleChangePeriod({ startDate: '', endDate: '' });
                    setState({ from: undefined, to: undefined, hoverFrom: undefined, hoverTo: undefined });
                  }}
                  className="ml-auto"
                />
              ) : null}
            </div>
          );
        }}
        sideOffset={4}
        withBorder
      >
        {({ closePopover }) => (
          <div className="shadow-container">
            <div className="flex h-full">
              {CustomRange}
              <WithGlobalStyles>
                <DayPicker
                  role="dialog"
                  mode="range"
                  numberOfMonths={2}
                  selected={{
                    from: from || undefined,
                    to: to || undefined
                  }}
                  onSelect={() => void 0} // http://github.com/gpbl/react-day-picker/issues/2690
                  onDayClick={handleDayClick}
                  locale={getDatePickerLocale(i18n.language)}
                  onDayMouseEnter={handleDayMouseEnter}
                  defaultMonth={from || new Date()}
                  weekStartsOn={1}
                  disabled={disabledRange}
                  formatters={{
                    formatCaption: (captionLabelDate) => localeUtils.formatMonthTitle(captionLabelDate, i18n.language)
                  }}
                  components={{
                    Nav: CustomDayPickerNav
                  }}
                />
              </WithGlobalStyles>
            </div>
            <ActionButtons
              primaryButtonProps={{
                onClick: handleUpdate,
                type
              }}
              secondaryButtonProps={{
                onClick: closePopover
              }}
              buttonHeight="32px"
              hasTopBorder
            >
              <>
                <span onClick={handleResetClick} className="me-auto cursor-pointer hover:underline">
                  {t('Button.Clear')}
                </span>
                {resultInput}
              </>
            </ActionButtons>
          </div>
        )}
      </Popover>
    </div>
  );
};
